"use client";

import React, { useState, useMemo, useEffect } from "react";
import {
  IoPricetagOutline,
  IoTimeOutline,
  IoSearchOutline,
  IoClose,
  IoChevronDown,
} from "react-icons/io5";
import { REST_API_URL } from "../api/config";

const BASE_URL = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) => CURRENCY_SYMBOL_MAP[code] || code || "$";

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const fetchUserInfo = async (token) => {
  try {
    const cachedData = localStorage.getItem("userInfo");
    const cacheTime = localStorage.getItem("userInfoTimestamp");
    const currentTime = Date.now();

    const headers = buildHeaders(token);

    const response = await fetch(`${BASE_URL}/auth/userinfo/`, { headers });

    const raw = await response.text();

    if (!response.ok) return { currency: "USD" };

    const userData = JSON.parse(raw);

    localStorage.setItem("userInfo", JSON.stringify(userData));
    localStorage.setItem("userInfoTimestamp", String(Date.now()));

    return userData;
  } catch (error) {
    console.error("Error fetching user info:", error);
    return { currency: "USD" };
  }
};

export default function CalendarDayModal({
  open,
  onClose,
  appointments,
  date,
  userStatus,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  useEffect(() => {
    const getUserInfo = async () => {
      if (!open) return;

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (token) {
        const userInfo = await fetchUserInfo(token);
        setUserCurrency(userInfo?.currency || "USD");

        const browserTimeZone = getUserTimeZone();
        setUserTimeZone(browserTimeZone);
      }
    };

    getUserInfo();
  }, [open]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
        document.body.style.overflow = "hidden";
      }, 10);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        document.body.style.overflow = "unset";
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const workers = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      const master =
        a.master || (userStatus === "individual" ? "You" : "Master");

      if (!map[master]) {
        map[master] = {
          id: master,
          name: master,
          avatar: a.worker_avatar,
          count: 1,
        };
      } else {
        map[master].count += 1;
      }
    });

    return Object.values(map);
  }, [appointments, userStatus]);

  const filteredAppointments = useMemo(() => {
    if (selectedWorkers.length === 0) return appointments;

    return appointments.filter((a) => {
      const master =
        a.master || (userStatus === "individual" ? "You" : "Master");
      return selectedWorkers.includes(master);
    });
  }, [appointments, selectedWorkers, userStatus]);

  const filteredWorkers = workers.filter((w) =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const toggleWorker = (id) => {
    setSelectedWorkers((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id],
    );
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDisplayPrice = (price) => {
    if (!price && price !== 0) return "0.00";

    try {
      const priceNum = typeof price === "string" ? parseFloat(price) : price;
      if (isNaN(priceNum)) return "0.00";

      return priceNum.toFixed(2);
    } catch (error) {
      console.error("Error formatting price:", error);
      return "0.00";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const today = date || new Date();
      const dateString = today.toISOString().split("T")[0];
      const fullDateTime = `${dateString}T${timeString}`;
      const dateObj = new Date(fullDateTime);

      return dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: userTimeZone,
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeString;
    }
  };

  const formatDateWithTimeZone = (dateObj) => {
    if (!dateObj) return "";
    try {
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: userTimeZone,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const formatMonthYear = (dateObj) => {
    if (!dateObj) return "";
    try {
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: userTimeZone,
      });
    } catch (error) {
      console.error("Error formatting month/year:", error);
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className={`relative w-full max-w-5xl h-[85vh] mx-4 
    bg-white 
    rounded-[20px] 
    overflow-hidden 
    shadow-2xl 
    flex flex-col 
    transition-all duration-300 transform
    ${isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b bg-white">
          <div>
            <h2 className="text-2xl font-normal text-gray-900">
              Appointments for{" "}
              {date ? formatDateWithTimeZone(date) : "Selected Date"}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredAppointments.length} appointment
              {filteredAppointments.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          >
            <IoClose className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* {userStatus !== "individual" && workers.length > 0 && (
          <div className="relative flex-shrink-0 p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center -space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                {workers.slice(0, 3).map((w, i) => (
                  <div key={w.id} className="relative">
                    <img
                      src={w.avatar}
                      alt={w.name}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {w.count}
                    </div>
                  </div>
                ))}
                {workers.length > 3 && (
                  <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-sm font-medium text-gray-700">
                    +{workers.length - 3}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="font-medium text-gray-700">
                  {workers.length === 1 ? "Worker" : "All Workers"}
                </span>
                <IoChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    showDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {selectedWorkers.length > 0 && (
                <div className="ml-auto">
                  <button
                    onClick={() => setSelectedWorkers([])}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-1 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    Clear filters ({selectedWorkers.length})
                  </button>
                </div>
              )}
            </div>

            {showDropdown && (
              <div className="absolute top-full left-4 right-4 mt-2 z-10">
                <div
                  className={`bg-white rounded-lg shadow-lg border overflow-hidden transition-all duration-200 ${
                    showDropdown
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-2 pointer-events-none"
                  }`}
                >
                  <div className="p-3 border-b">
                    <div className="relative">
                      <IoSearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search workers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:scale-110 transition-transform"
                        >
                          <IoClose className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto">
                    {filteredWorkers.map((w) => {
                      const isSelected = selectedWorkers.includes(w.id);
                      return (
                        <div
                          key={w.id}
                          onClick={() => toggleWorker(w.id)}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            isSelected ? "bg-blue-50" : ""
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                              isSelected
                                ? "bg-blue-500 border-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <img
                            src={w.avatar}
                            alt={w.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <span className="flex-1 font-medium text-gray-900">
                            {w.name}
                          </span>
                          <div className="px-2 py-1 bg-gray-100 rounded text-sm font-medium text-gray-700">
                            {w.count} appt{w.count !== 1 ? "s" : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )} */}

        <div className="flex-1 overflow-y-auto p-6">
          {filteredAppointments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAppointments.map((a, index) => {
                const currencyCode = a.currency || userCurrency || "USD";
                const currencySymbol = getCurrencySymbol(currencyCode);
                const displayPrice = formatDisplayPrice(a.price);

                return (
                  <div
                    key={a.id}
                    className="group bg-white rounded-xl border p-5 hover:shadow-lg transition-all duration-300 hover:border-blue-300"
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: "slideIn 0.3s ease-out forwards",
                      opacity: 0,
                      transform: "translateY(10px)",
                    }}
                  >
                    <div className="mb-4">
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={a.avatar}
                          alt="client"
                          className="w-12 h-12 rounded-full border-2 border-white shadow"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {a.name}
                          </h3>
                          <p className="text-sm text-blue-600 truncate">
                            {a.service}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Status:{" "}
                            <span
                              className={`font-medium ${
                                a.status?.toLowerCase() === "upcoming"
                                  ? "text-green-600"
                                  : a.status?.toLowerCase() === "completed"
                                    ? "text-blue-600"
                                    : "text-gray-600"
                              }`}
                            >
                              {a.status}
                            </span>
                          </p>
                        </div>
                      </div>
                      {a.comment && a.comment !== "No comment provided" && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg mt-2 h-auto">
                          {a.comment}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 text-sm hover:translate-x-1 transition-transform">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <IoPricetagOutline className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-gray-700 font-medium">
                            {currencySymbol}
                            {displayPrice}
                          </div>
                          <div className="text-xs text-gray-500">
                            Total amount
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-sm hover:translate-x-1 transition-transform">
                        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                          <IoTimeOutline className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-gray-700 font-medium">
                            {formatTime(a.timeStart)} – {formatTime(a.timeEnd)}
                          </div>
                          <div className="text-xs text-gray-500">Duration</div>
                        </div>
                      </div>
                    </div>

                    {/* {userStatus !== "individual" && a.master && (
                      <>
                        <div className="h-px bg-gray-200 my-4" />
                        <div className="flex items-center justify-between hover:translate-x-1 transition-transform">
                          <div className="flex items-center gap-3">
                            <img
                              src={a.worker_avatar}
                              alt="worker"
                              className="w-8 h-8 rounded-full border-2 border-white"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {a.master}
                              </span>
                              <div className="text-xs text-gray-500">
                                Assigned
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )} */}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No appointments found
              </h3>
              <p className="text-gray-600 max-w-md mb-6">
                {selectedWorkers.length > 0
                  ? "No appointments match the selected workers. Try changing your filters."
                  : "There are no appointments scheduled for this date."}
              </p>
              {selectedWorkers.length > 0 && (
                <button
                  onClick={() => setSelectedWorkers([])}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 shadow hover:shadow-md"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 p-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">{filteredAppointments.length}</span>{" "}
              of {appointments.length} appointments
              {selectedWorkers.length > 0 && (
                <span className="ml-2">
                  (filtered by {selectedWorkers.length} worker
                  {selectedWorkers.length !== 1 ? "s" : ""})
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {selectedWorkers.length > 0 && (
                <button
                  onClick={() => setSelectedWorkers([])}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
    </div>
  );
}
